import type {
  DevexReviewLatencyGroupBy,
  DevexReviewLatencyPoint,
  DevexReviewLatencyResponse,
  DevexReviewLatencySizeBucket,
} from '@dev-console/types';
import { useQuery } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import type { LegendProps, TooltipProps } from 'recharts';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import {
  formatAxisHours,
  formatChartDate,
  formatCount,
  formatDeltaPerDay,
  formatHours,
  formatPerDay,
  formatSignedHours,
} from '@/components/devex-metrics/review-latency-formatters';
import { GROUP_BY_LABELS } from '@/components/devex-metrics/review-latency-labels';
import {
  filterTrailingDays,
  formatGroupValue,
  getGroupKey,
  getTrailingAverage,
  getTrendWindows,
  sortGroupKeys,
  summarizeTrailingWindow,
} from '@/components/devex-metrics/review-latency-utils';
import { EmptyState, ErrorBanner, SkeletonBlock } from '@/components/ui/state-views';
import { getReviewLatencySeries } from '@/config/api-helpers';

type TrendLineConfig = {
  color: string;
  key: string;
  name: string;
  strokeDasharray?: string;
};

type DeltaTone = 'lower-is-better' | 'neutral';

type OrgTrendPoint = DevexReviewLatencyPoint & {
  rollingP50: number | null;
  rollingP90: number | null;
};

type ComparisonTrendPoint = DevexReviewLatencyPoint & {
  orgP50: number | null;
  rollingGroupP50: number | null;
  rollingGroupP90: number | null;
  rollingOrgP50: number | null;
};

const SAMPLE_BAR_COLOR = '#E2E8F0';
const COLOR_P50 = '#285276';
const COLOR_P90 = '#9B4728';
const COLOR_NEUTRAL = '#475569';
const COLOR_SPREAD = '#6B5F22';
const COLOR_POSITIVE = '#417428';
const STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes

const ORG_TREND_LINES: TrendLineConfig[] = [
  { key: 'rollingP50', name: 'Median (p50)', color: COLOR_P50 },
  { key: 'rollingP90', name: 'Tail (p90)', color: COLOR_P90 },
];

const COMPARISON_TREND_LINES: TrendLineConfig[] = [
  { key: 'rollingGroupP50', name: 'Group median (p50)', color: COLOR_P50 },
  { key: 'rollingOrgP50', name: 'Org median (p50)', color: '#64748B', strokeDasharray: '5 4' },
  { key: 'rollingGroupP90', name: 'Group tail (p90)', color: COLOR_P90 },
];

function toHours(p: DevexReviewLatencyPoint): DevexReviewLatencyPoint {
  return {
    ...p,
    avg: p.avg != null ? p.avg / 3600 : null,
    p50: p.p50 != null ? p.p50 / 3600 : null,
    p75: p.p75 != null ? p.p75 / 3600 : null,
    p90: p.p90 != null ? p.p90 / 3600 : null,
    p99: p.p99 != null ? p.p99 / 3600 : null,
  };
}

function getDeltaTextColor(delta: number | null, tone: DeltaTone): string {
  if (delta == null || delta === 0 || tone === 'neutral') return 'text-neutral-500';
  return delta < 0 ? 'text-emerald-700' : 'text-amber-800';
}

function TrendTooltip({ active, payload, label }: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;

  const orderedPayload = [...payload].sort((a, b) =>
    a.dataKey === 'sampleSizePrs' ? -1 : b.dataKey === 'sampleSizePrs' ? 1 : 0
  );

  return (
    <div className='rounded-lg border border-neutral-200 bg-white px-3 py-2.5 shadow-lg'>
      <p className='font-mediumbold mb-2 text-xs text-neutral-600'>{formatChartDate(String(label))}</p>
      <div className='space-y-1'>
        {orderedPayload.map((entry) => {
          const isCountSeries = entry.dataKey === 'sampleSizePrs';
          const value =
            typeof entry.value === 'number'
              ? isCountSeries
                ? formatCount(entry.value)
                : formatHours(entry.value)
              : '—';

          return (
            <div key={String(entry.dataKey)} className='flex items-center gap-2 text-xs'>
              <span
                className='size-2 shrink-0 rounded-full'
                style={{ backgroundColor: String(entry.color ?? SAMPLE_BAR_COLOR) }}
              />
              <span className='text-neutral-500'>{entry.name}</span>
              <span className='font-mediumbold ml-auto pl-4 text-neutral-900'>{value}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ComparisonTooltipDetails({ groupBy }: { groupBy: DevexReviewLatencyGroupBy }) {
  if (groupBy === 'team') {
    return (
      <>
        <li>PRs use the author's GitHub team membership when data is collected.</li>
        <li>Other includes authors with no listed team in the collected data.</li>
        <li>Authors on multiple teams count in each team; overall totals count each PR once.</li>
      </>
    );
  }

  if (groupBy === 'size') {
    return <li>PR size uses additions + deletions: XS {'<=50'}, S 51-200, M 201-500, L 501-1000, XL &gt;1000 LOC.</li>;
  }

  if (groupBy === 'relationship') {
    return (
      <>
        <li>
          <span className='font-mediumbold text-neutral-700'>In team</span>: at least one requested reviewer team
          matches one of the author's tracked teams.
        </li>
        <li>
          <span className='font-mediumbold text-neutral-700'>Cross-team</span>: a reviewer team was requested, but none
          match the author's tracked teams.
        </li>
        <li>
          <span className='font-mediumbold text-neutral-700'>No team tag</span>: no reviewer team was requested before
          the first human review.
        </li>
      </>
    );
  }

  if (groupBy === 'assignment') {
    return (
      <>
        <li>
          Assignment mode is based on reviewer requests before first human review: individual-only, team-only, mixed, or
          none.
        </li>
        <li>Mixed means at least one person and at least one team were requested.</li>
      </>
    );
  }

  if (groupBy === 'hourOfDay') {
    return <li>Opened hour is bucketed in ET: business hours are 9am-5pm; everything else is off-hours.</li>;
  }

  if (groupBy === 'dayOfWeek') {
    return <li>Opened day is bucketed in ET as Mon-Thu, Friday, or weekend.</li>;
  }

  if (groupBy === 'surface') {
    return (
      <li>Touch surface is derived from changed file paths; multi-surface means the PR spans more than one area.</li>
    );
  }

  return null;
}

function MetricTooltip({ groupBy }: { groupBy?: DevexReviewLatencyGroupBy }) {
  return (
    <ul className='list-disc space-y-1.5 pl-4'>
      <li>Time from when a PR was opened in a reviewable state to the first human review submission.</li>
      <li>Only time in a reviewable state counts. Draft time is excluded and bot reviews do not count.</li>
      {groupBy && <ComparisonTooltipDetails groupBy={groupBy} />}
    </ul>
  );
}

function InfoTooltip({ text }: { text: React.ReactNode }) {
  return (
    <div className='group relative inline-flex'>
      <span className='cursor-help text-neutral-400 hover:text-neutral-500'>
        <svg className='size-4' viewBox='0 0 20 20' fill='currentColor'>
          <path
            fillRule='evenodd'
            d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z'
            clipRule='evenodd'
          />
        </svg>
      </span>
      <div className='absolute left-6 top-0 z-10 hidden w-80 rounded-lg border border-neutral-200 bg-white p-3 text-xs leading-relaxed text-neutral-600 shadow-lg group-hover:block'>
        {text}
      </div>
    </div>
  );
}

function BoxLegend({ payload }: LegendProps) {
  if (!payload?.length) return null;

  return (
    <div className='mt-3 flex justify-center'>
      <div className='inline-flex flex-wrap justify-center gap-x-4 gap-y-2 rounded-lg border border-neutral-100 bg-white/50 px-4 py-2.5'>
        {payload.map((entry) => (
          <span key={String(entry.value)} className='inline-flex items-center gap-1.5 text-sm text-neutral-600'>
            <span
              className='size-2.5 shrink-0 rounded-full'
              style={{ backgroundColor: String(entry.color ?? SAMPLE_BAR_COLOR) }}
            />
            {entry.value}
          </span>
        ))}
      </div>
    </div>
  );
}

function HeaderPill({ text }: { text: string }) {
  return (
    <span className='rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-1 text-[11px] font-medium text-neutral-600'>
      {text}
    </span>
  );
}

function ReviewLatencySkeleton() {
  return (
    <div className='space-y-6' aria-label='Loading review latency metrics'>
      {[0, 1].map((section) => (
        <div key={section} className='rounded-lg border border-neutral-200 bg-white'>
          <div className='space-y-3 border-b border-neutral-200 px-6 py-4'>
            <div className='flex flex-wrap items-center gap-2'>
              <SkeletonBlock className='h-4 w-72 max-w-full' />
              <SkeletonBlock className='h-6 w-28 rounded-full' />
            </div>
            {section === 1 && (
              <div className='flex flex-wrap gap-2'>
                <SkeletonBlock className='h-8 w-24' />
                <SkeletonBlock className='h-8 w-28' />
                <SkeletonBlock className='h-8 w-24' />
              </div>
            )}
          </div>
          <div className='space-y-5 p-6'>
            <div className='grid gap-3 md:grid-cols-2 xl:grid-cols-4'>
              <SkeletonBlock className='h-28' />
              <SkeletonBlock className='h-28' />
              <SkeletonBlock className='h-28' />
              <SkeletonBlock className='h-28' />
            </div>
            <SkeletonBlock className={section === 0 ? 'h-80' : 'h-72'} />
          </div>
        </div>
      ))}
    </div>
  );
}

function SummaryCard({
  accentColor,
  delta,
  deltaFormatter,
  deltaLabel,
  deltaTone = 'lower-is-better',
  helperText,
  label,
  value,
}: {
  accentColor: string;
  delta: number | null;
  deltaFormatter: (value: number | null | undefined) => string;
  deltaLabel: string;
  deltaTone?: DeltaTone;
  helperText: string;
  label: string;
  value: string;
}) {
  return (
    <div className='rounded-md border border-neutral-200 bg-neutral-100/70 p-3.5'>
      <p className='text-xs text-neutral-500'>{label}</p>
      <p className='font-mediumbold mt-1 text-xl' style={{ color: accentColor }}>
        {value}
      </p>
      <p className='mt-1 text-xs text-neutral-500'>{helperText}</p>
      {delta != null && (
        <p className={`mt-2 text-xs ${getDeltaTextColor(delta, deltaTone)}`}>
          {deltaFormatter(delta)} vs {deltaLabel}
        </p>
      )}
    </div>
  );
}

function TrendChart({
  data,
  height = 320,
  lines,
}: {
  data: { date: string }[];
  height?: number;
  lines: TrendLineConfig[];
}) {
  return (
    <ResponsiveContainer width='100%' height={height}>
      <ComposedChart data={data} margin={{ top: 12, right: 8, bottom: 8, left: 0 }}>
        <CartesianGrid strokeDasharray='3 3' stroke='#f1f5f9' vertical={false} />
        <XAxis
          dataKey='date'
          tick={{ fontSize: 10, fill: '#94a3b8' }}
          tickFormatter={formatChartDate}
          tickLine={false}
          axisLine={false}
          minTickGap={28}
        />
        <YAxis
          yAxisId='latency'
          tick={{ fontSize: 10, fill: '#94a3b8' }}
          tickLine={false}
          axisLine={false}
          tickFormatter={formatAxisHours}
          width={44}
        />
        <YAxis
          yAxisId='volume'
          orientation='right'
          tick={{ fontSize: 10, fill: '#CBD5E1' }}
          tickLine={false}
          axisLine={false}
          tickFormatter={formatCount}
          width={36}
          allowDecimals={false}
        />
        <Tooltip content={<TrendTooltip />} />
        <Legend content={<BoxLegend />} />
        <Bar
          yAxisId='volume'
          dataKey='sampleSizePrs'
          name='PRs reviewed'
          fill={SAMPLE_BAR_COLOR}
          fillOpacity={0.7}
          radius={[4, 4, 0, 0]}
          barSize={12}
        />
        {lines.map((line) => (
          <Line
            key={line.key}
            yAxisId='latency'
            type='monotone'
            dataKey={line.key}
            name={line.name}
            stroke={line.color}
            strokeWidth={2.5}
            strokeDasharray={line.strokeDasharray}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 0 }}
            connectNulls={false}
          />
        ))}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export function ReviewLatencyView({
  days,
  excludeBots,
  groupBy,
  selectedGroup,
  setSelectedGroup,
  sizeBuckets,
  teamFilters,
}: {
  days: number;
  excludeBots: boolean;
  groupBy: DevexReviewLatencyGroupBy;
  selectedGroup: string;
  setSelectedGroup: (group: string) => void;
  sizeBuckets: DevexReviewLatencySizeBucket[];
  teamFilters: string[];
}) {
  const { comparisonWindowDays, groupSmoothingWindowDays, orgSmoothingWindowDays, queryDays } = getTrendWindows(days);
  const comparisonWindowLabel = `${comparisonWindowDays}d`;
  const orgSmoothingWindowLabel = `${orgSmoothingWindowDays}d`;
  const groupSmoothingWindowLabel = `${groupSmoothingWindowDays}d`;
  const recentWindowLabel = `recent ${comparisonWindowLabel}`;
  const previousWindowLabel = `previous ${comparisonWindowLabel}`;

  const orgQuery = useQuery<DevexReviewLatencyResponse>({
    queryKey: ['devex-review-latency', 'org', days, queryDays, excludeBots, teamFilters, sizeBuckets],
    queryFn: () =>
      getReviewLatencySeries({
        scope: 'org',
        days: queryDays,
        excludeBots: excludeBots ? true : undefined,
        teams: teamFilters,
        sizeBuckets,
      }),
    staleTime: STALE_TIME_MS,
  });

  const comparisonQuery = useQuery<DevexReviewLatencyResponse>({
    queryKey: ['devex-review-latency', 'comparison', groupBy, days, queryDays, excludeBots, teamFilters, sizeBuckets],
    queryFn: () =>
      getReviewLatencySeries({
        scope: 'team',
        days: queryDays,
        groupBy: groupBy === 'team' ? undefined : groupBy,
        excludeBots: excludeBots ? true : undefined,
        teams: teamFilters,
        sizeBuckets,
      }),
    staleTime: STALE_TIME_MS,
  });

  const groupKeys = useMemo(() => {
    if (!comparisonQuery.data) return [];
    return sortGroupKeys([...new Set(comparisonQuery.data.series.map(getGroupKey).filter(Boolean))], groupBy);
  }, [comparisonQuery.data, groupBy]);

  const resolvedGroup = groupKeys.includes(selectedGroup) ? selectedGroup : groupKeys[0] ?? '';

  const orgDailyData = useMemo(() => (orgQuery.data?.series ?? []).map(toHours), [orgQuery.data]);

  const orgTrendData = useMemo<OrgTrendPoint[]>(
    () =>
      orgDailyData.map((point, index, series) => ({
        ...point,
        rollingP50: getTrailingAverage(series, index, orgSmoothingWindowDays, (item) => item.p50),
        rollingP90: getTrailingAverage(series, index, orgSmoothingWindowDays, (item) => item.p90),
      })),
    [orgDailyData, orgSmoothingWindowDays]
  );
  const orgVisibleTrendData = useMemo(() => filterTrailingDays(orgTrendData, days), [days, orgTrendData]);

  const orgByDate = useMemo(() => new Map(orgDailyData.map((point) => [point.date, point])), [orgDailyData]);

  const comparisonDailyData = useMemo<ComparisonTrendPoint[]>(
    () =>
      (comparisonQuery.data?.series ?? [])
        .filter((point) => getGroupKey(point) === resolvedGroup)
        .map((point) => {
          const orgPoint = orgByDate.get(point.date);
          const hourPoint = toHours(point);
          return {
            ...hourPoint,
            orgP50: orgPoint?.p50 ?? null,
            rollingGroupP50: null,
            rollingGroupP90: null,
            rollingOrgP50: null,
          };
        }),
    [comparisonQuery.data, orgByDate, resolvedGroup]
  );

  const comparisonTrendData = useMemo<ComparisonTrendPoint[]>(
    () =>
      comparisonDailyData.map((point, index, series) => ({
        ...point,
        rollingOrgP50: getTrailingAverage(series, index, groupSmoothingWindowDays, (item) => item.orgP50),
        rollingGroupP50: getTrailingAverage(series, index, groupSmoothingWindowDays, (item) => item.p50),
        rollingGroupP90: getTrailingAverage(series, index, groupSmoothingWindowDays, (item) => item.p90),
      })),
    [comparisonDailyData, groupSmoothingWindowDays]
  );
  const comparisonVisibleTrendData = useMemo(
    () => filterTrailingDays(comparisonTrendData, days),
    [comparisonTrendData, days]
  );

  const isLoading = orgQuery.isLoading || comparisonQuery.isLoading;
  const error = orgQuery.error ?? comparisonQuery.error;

  const { orgMedianSummary, orgTailSummary, orgVolumeSummary, orgSpreadSummary } = useMemo(
    () => ({
      orgMedianSummary: summarizeTrailingWindow(orgDailyData, comparisonWindowDays, (point) => point.p50),
      orgTailSummary: summarizeTrailingWindow(orgDailyData, comparisonWindowDays, (point) => point.p90),
      orgVolumeSummary: summarizeTrailingWindow(orgDailyData, comparisonWindowDays, (point) => point.sampleSizePrs, {
        denominator: 'calendar-days',
      }),
      orgSpreadSummary: summarizeTrailingWindow(orgDailyData, comparisonWindowDays, (point) =>
        point.p90 != null && point.p50 != null ? point.p90 - point.p50 : null
      ),
    }),
    [comparisonWindowDays, orgDailyData]
  );

  const { groupMedianSummary, groupTailSummary, groupVolumeSummary, groupVsOrgMedianSummary } = useMemo(
    () => ({
      groupMedianSummary: summarizeTrailingWindow(comparisonDailyData, comparisonWindowDays, (point) => point.p50),
      groupTailSummary: summarizeTrailingWindow(comparisonDailyData, comparisonWindowDays, (point) => point.p90),
      groupVolumeSummary: summarizeTrailingWindow(
        comparisonDailyData,
        comparisonWindowDays,
        (point) => point.sampleSizePrs,
        {
          denominator: 'calendar-days',
        }
      ),
      groupVsOrgMedianSummary: summarizeTrailingWindow(comparisonDailyData, comparisonWindowDays, (point) =>
        point.p50 != null && point.orgP50 != null ? point.p50 - point.orgP50 : null
      ),
    }),
    [comparisonDailyData, comparisonWindowDays]
  );

  if (isLoading) return <ReviewLatencySkeleton />;
  if (error) {
    return (
      <ErrorBanner
        message={error.message}
        onRetry={() => {
          void orgQuery.refetch();
          void comparisonQuery.refetch();
        }}
        title='Metrics are unavailable'
        className='sticky top-3 z-20 my-4 shadow-sm'
      />
    );
  }

  return (
    <div className='space-y-6'>
      <div className='rounded-lg border border-neutral-200 bg-white'>
        <div className='border-b border-neutral-200 px-6 py-4'>
          <div className='flex flex-wrap items-center gap-2'>
            <div className='flex items-center gap-1.5'>
              <h3 className='font-mediumbold text-sm text-neutral-900'>
                Time to First Human Review — Organization Trend
              </h3>
              <InfoTooltip text={<MetricTooltip />} />
            </div>
            <HeaderPill text={`Rolling ${orgSmoothingWindowLabel} avg`} />
          </div>
        </div>
        <div className='p-6'>
          {orgVisibleTrendData.length === 0 ? (
            <EmptyState message='No data for this time range.' />
          ) : (
            <div className='space-y-5'>
              <div className='grid gap-3 md:grid-cols-2 xl:grid-cols-4'>
                <SummaryCard
                  accentColor={COLOR_P50}
                  delta={orgMedianSummary.delta}
                  deltaFormatter={formatSignedHours}
                  deltaLabel={`${previousWindowLabel} avg daily p50`}
                  helperText={`Avg daily p50 over the ${recentWindowLabel}`}
                  label='Median review time'
                  value={formatHours(orgMedianSummary.current)}
                />
                <SummaryCard
                  accentColor={COLOR_P90}
                  delta={orgTailSummary.delta}
                  deltaFormatter={formatSignedHours}
                  deltaLabel={`${previousWindowLabel} avg daily p90`}
                  helperText={`Avg daily p90 over the ${recentWindowLabel}`}
                  label='Tail review time (p90)'
                  value={formatHours(orgTailSummary.current)}
                />
                <SummaryCard
                  accentColor={COLOR_SPREAD}
                  delta={orgSpreadSummary.delta}
                  deltaFormatter={formatSignedHours}
                  deltaLabel={`${previousWindowLabel} avg daily spread`}
                  helperText={`Avg daily p90 - p50 spread over the ${recentWindowLabel}`}
                  label='p90 - p50 spread'
                  value={formatHours(orgSpreadSummary.current)}
                />
                <SummaryCard
                  accentColor={COLOR_NEUTRAL}
                  delta={orgVolumeSummary.delta}
                  deltaFormatter={formatDeltaPerDay}
                  deltaLabel={`${previousWindowLabel} avg daily PR volume`}
                  deltaTone='neutral'
                  helperText={`Avg PRs first-reviewed per day over the ${recentWindowLabel}`}
                  label='PRs reviewed / day'
                  value={formatPerDay(orgVolumeSummary.current)}
                />
              </div>
              <TrendChart data={orgVisibleTrendData} lines={ORG_TREND_LINES} />
            </div>
          )}
        </div>
      </div>

      <div className='rounded-lg border border-neutral-200 bg-white'>
        <div className='space-y-3 border-b border-neutral-200 px-6 py-4'>
          <div className='flex flex-wrap items-center gap-2'>
            <div className='flex items-center gap-1.5'>
              <h3 className='font-mediumbold text-sm text-neutral-900'>
                Time to First Human Review — {GROUP_BY_LABELS[groupBy]} Comparison
              </h3>
              <InfoTooltip text={<MetricTooltip groupBy={groupBy} />} />
            </div>
            <HeaderPill text={`Rolling ${groupSmoothingWindowLabel} avg`} />
          </div>
          {groupKeys.length > 0 && (
            <div className='flex flex-wrap gap-2'>
              {groupKeys.map((group) => (
                <button
                  key={group}
                  onClick={() => setSelectedGroup(group)}
                  className={`rounded px-3 py-1 text-sm transition-colors ${
                    resolvedGroup === group
                      ? 'bg-neutral-900 text-white'
                      : 'border border-neutral-200 bg-white text-neutral-600 hover:border-neutral-400'
                  }`}
                >
                  {formatGroupValue(groupBy, group)}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className='p-6'>
          {comparisonVisibleTrendData.length === 0 ? (
            <EmptyState message='No data for this comparison.' />
          ) : (
            <div className='space-y-5'>
              <div className='grid gap-3 md:grid-cols-2 xl:grid-cols-4'>
                <SummaryCard
                  accentColor={
                    groupVsOrgMedianSummary.current == null
                      ? COLOR_NEUTRAL
                      : groupVsOrgMedianSummary.current <= 0
                      ? COLOR_POSITIVE
                      : COLOR_P90
                  }
                  delta={groupVsOrgMedianSummary.delta}
                  deltaFormatter={formatSignedHours}
                  deltaLabel={`${previousWindowLabel} avg daily group-org gap`}
                  helperText={`Group median compared to the org baseline over the ${recentWindowLabel}`}
                  label='Group minus org median'
                  value={formatSignedHours(groupVsOrgMedianSummary.current)}
                />
                <SummaryCard
                  accentColor={COLOR_P50}
                  delta={groupMedianSummary.delta}
                  deltaFormatter={formatSignedHours}
                  deltaLabel={`${previousWindowLabel} avg daily group p50`}
                  helperText={`Avg daily group p50 over the ${recentWindowLabel}`}
                  label='Group median review time'
                  value={formatHours(groupMedianSummary.current)}
                />
                <SummaryCard
                  accentColor={COLOR_P90}
                  delta={groupTailSummary.delta}
                  deltaFormatter={formatSignedHours}
                  deltaLabel={`${previousWindowLabel} avg daily group p90`}
                  helperText={`Avg daily group p90 over the ${recentWindowLabel}`}
                  label='Group tail review time (p90)'
                  value={formatHours(groupTailSummary.current)}
                />
                <SummaryCard
                  accentColor={COLOR_NEUTRAL}
                  delta={groupVolumeSummary.delta}
                  deltaFormatter={formatDeltaPerDay}
                  deltaLabel={`${previousWindowLabel} avg daily PR volume`}
                  deltaTone='neutral'
                  helperText={`Avg PRs first-reviewed per day over the ${recentWindowLabel}`}
                  label='PRs reviewed / day'
                  value={formatPerDay(groupVolumeSummary.current)}
                />
              </div>
              <div className='rounded-lg border border-neutral-100 bg-neutral-50/40 p-4'>
                <p className='mb-3 text-xs text-neutral-500'>
                  Supporting trend context only. Use the cards above for the main comparison read.
                </p>
                <TrendChart data={comparisonVisibleTrendData} height={260} lines={COMPARISON_TREND_LINES} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
